import React from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";

// EPA AQI category colors
export function aqiColor(aqi) {
  if (aqi == null) return "#9CA3AF";
  if (aqi <= 50) return "#00E400";
  if (aqi <= 100) return "#FFFF00";
  if (aqi <= 150) return "#FF7E00";
  if (aqi <= 200) return "#FF0000";
  if (aqi <= 300) return "#8F3F97";
  return "#7E0023";
}

function aqiLabel(aqi) {
  if (aqi == null) return "No data";
  if (aqi <= 50) return "Good";
  if (aqi <= 100) return "Moderate";
  if (aqi <= 150) return "Unhealthy for Sensitive Groups";
  if (aqi <= 200) return "Unhealthy";
  if (aqi <= 300) return "Very Unhealthy";
  return "Hazardous";
}

function num(v) {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? Math.round(n) : null;
}

function siteAqi(p) {
  return num(p.OZONEPM_AQI) ?? num(p.PM25_AQI) ?? num(p.OZONE_AQI) ?? num(p.PM10_AQI);
}

function aqiIcon(aqi) {
  const color = aqiColor(aqi);
  const dark = aqi != null && (aqi > 150 || (aqi > 200));
  const textColor = aqi != null && aqi > 150 ? "#FFFFFF" : "#0A0D0C";
  return L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:50%;background:${color};
      border:2px solid #0A0D0C;display:flex;align-items:center;justify-content:center;
      font:700 10px monospace;color:${textColor};box-shadow:0 1px 3px rgba(0,0,0,0.5);">
      ${aqi ?? "–"}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -12],
  });
}

export default function AqiLayer({ aqi }) {
  const features = aqi?.features || [];
  return features.map((f, i) => {
    const p = f.properties || {};
    const [lon, lat] = f.geometry?.coordinates || [];
    if (lat == null) return null;
    const val = siteAqi(p);
    return (
      <Marker
        key={`aqi-${p.SiteName || i}`}
        position={[lat, lon]}
        icon={aqiIcon(val)}
        data-testid={`aqi-marker-${i}`}
      >
        <Popup>
          <div className="min-w-[190px]" data-testid="aqi-popup">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1 text-tac-dim">
              AirNow Monitor
            </div>
            <div className="font-display font-bold text-sm text-tac-text mb-1">
              {p.SiteName || "Monitor"}
            </div>
            <div className="text-[13px] font-bold" style={{ color: aqiColor(val) }}>
              AQI {val ?? "—"} · {aqiLabel(val)}
            </div>
            <div className="text-[11px] text-tac-dim font-mono-tac mt-1 space-y-0.5">
              {num(p.PM25_AQI) != null && <div>PM2.5 AQI: {num(p.PM25_AQI)}</div>}
              {num(p.OZONE_AQI) != null && <div>Ozone AQI: {num(p.OZONE_AQI)}</div>}
              {p.LocalTimeString && <div>Updated {p.LocalTimeString}</div>}
            </div>
            <div className="text-[10px] font-mono-tac text-tac-faint mt-2 pt-1 border-t border-tac-border/60">
              Source: EPA AirNow{p.DataSource ? ` · ${p.DataSource}` : ""}
            </div>
          </div>
        </Popup>
      </Marker>
    );
  });
}
